import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { SINGIN_API } from "./APIHooks";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const signIn = async (email, password) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      
      if (!apiKey) {
        console.error("API key is missing");
        return { 
          success: false, 
          message: "Configuration error. Please try again." 
        };
      }

      const response = await fetch(SINGIN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          emailaddress: email,
          password: password,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === "success") {
        const userData = {
          user_id: data.data.user_id,
          name: data.data.name,
          emailaddress: data.data.emailaddress,
          phonenumber: data.data.phonenumber,
          role: data.data.role,
          sex: data.data.sex,
          address: data.data.address,
          is_active: data.data.is_active,
        };
        
        // Store user data
        localStorage.setItem("userToken", JSON.stringify(userData));
        setUser(userData);
        
        // Navigate to dashboard
        navigate("/user/dashboard");
        
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error("Signin error:", error);
      return {
        success: false,
        message: error.message || "An error occurred. Please try again.",
      };
    }
  };

  const signOut = () => {
    localStorage.removeItem("userToken");
    setUser(null);
    navigate("/signin");
  };

  // Alias methods for compatibility
  const login = signIn;
  const logout = signOut;

  const loadUser = () => {
    try {
      const userToken = localStorage.getItem("userToken");
      if (userToken) {
        setUser(JSON.parse(userToken));
      }
    } catch (error) {
      console.error("Error loading user:", error);
      localStorage.removeItem("userToken");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      signIn, 
      signOut, 
      login,    // Alias for compatibility
      logout,   // Alias for compatibility
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);